/**
 * @fileoverview Address Validation Badge Component
 * 
 * Visual indicator showing address validation status with optional
 * verify button for triggering on-demand validation.
 */

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { CheckCircle2, AlertCircle, XCircle, HelpCircle, Loader2 } from 'lucide-react';
import { Deliverability } from '@/hooks/useAddressValidation';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

interface AddressValidationBadgeProps {
  /** Whether the address has been validated */
  isValidated: boolean;
  /** Deliverability status from Smarty */
  deliverability?: Deliverability;
  /** Callback to trigger validation */
  onVerify?: () => void;
  /** Whether validation is in progress */
  isVerifying?: boolean;
  /** Show compact version (icon only) */
  compact?: boolean;
  /** Additional CSS classes */
  className?: string;
}

export function AddressValidationBadge({
  isValidated,
  deliverability,
  onVerify,
  isVerifying = false,
  compact = false,
  className = '',
}: AddressValidationBadgeProps) {
  // Determine badge variant and content based on status
  const getBadgeConfig = () => {
    if (isVerifying) {
      return {
        variant: 'secondary' as const,
        icon: <Loader2 className="h-3 w-3 animate-spin" />,
        label: 'Verifying...',
        tooltip: 'Address validation in progress',
      };
    }

    if (!isValidated) {
      return {
        variant: 'outline' as const,
        icon: <HelpCircle className="h-3 w-3" />,
        label: 'Unverified',
        tooltip: 'This address has not been verified yet',
      };
    }

    switch (deliverability) {
      case 'deliverable':
        return {
          variant: 'default' as const,
          icon: <CheckCircle2 className="h-3 w-3" />,
          label: 'Verified',
          tooltip: 'This address has been verified as deliverable',
          className: 'bg-green-100 text-green-800 border-green-200 hover:bg-green-100',
        };
      case 'partially_deliverable':
        return {
          variant: 'default' as const,
          icon: <AlertCircle className="h-3 w-3" />,
          label: 'Partial',
          tooltip: 'This address exists but may have delivery issues (e.g., missing unit number)',
          className: 'bg-yellow-100 text-yellow-800 border-yellow-200 hover:bg-yellow-100',
        };
      case 'undeliverable':
        return {
          variant: 'destructive' as const,
          icon: <XCircle className="h-3 w-3" />,
          label: 'Invalid',
          tooltip: 'This address could not be verified. It may not be deliverable.',
        };
      default:
        return {
          variant: 'secondary' as const,
          icon: <HelpCircle className="h-3 w-3" />,
          label: 'Unknown',
          tooltip: 'Address validation status unknown',
        };
    }
  };

  const config = getBadgeConfig();

  const badge = (
    <Badge
      variant={config.variant}
      className={`flex items-center gap-1 ${config.className || ''} ${className}`}
    >
      {config.icon}
      {!compact && <span>{config.label}</span>}
    </Badge>
  );

  const content = (
    <div className="flex items-center gap-2">
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            {badge}
          </TooltipTrigger>
          <TooltipContent>
            <p>{config.tooltip}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      
      {onVerify && !isValidated && !isVerifying && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onVerify}
          className="h-6 px-2 text-xs"
        >
          Verify
        </Button>
      )}
    </div>
  );

  return content;
}
